"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const number_utils_1 = require("./number-utils");
/**
 * The formula for calculating fees and costs is as follows:
 *
 *   Nurse pay rate * Platform fee * Employer fee = Total rate
 *
 * Given a nurse gross rate of $50, a platform fee of 5%, and an employer fee of 10%:
 *
 *   50 * 1.05 * 1.1 = 57.75 Total Rate
 *
 * We work backwards from the total rate to determine the pay rate because the total
 * rate is what is entered by the facility:
 *
 *   Total rate / Platform fee / Employer fee = Nurse pay rate
 *
 *   57.75 / 1.05 / 1.1 = 50 Nurse pay rate
 *
 * All monetary numbers in this class are in minor units (pennies).
 */
class CostCalculator {
    inputRate;
    platformFeePct;
    employerFeePct;
    minutesWorked;
    stipend;
    constructor({ inputRate, platformFeePct, employerFeePct, minutesWorked, stipend = 0, }) {
        this.inputRate = inputRate;
        this.platformFeePct = platformFeePct;
        this.employerFeePct = employerFeePct;
        this.minutesWorked = minutesWorked;
        this.stipend = stipend;
    }
    get payRate() {
        return this.inputRate - (this.platformFeeRate + this.employerFeeRate);
    }
    get platformFeeRate() {
        return (this.inputRate -
            Math.round(this.inputRate / (1 + this.platformFeePct)));
    }
    get employerFeeRate() {
        const minusPlatformFee = this.inputRate - this.platformFeeRate;
        return (minusPlatformFee -
            Math.round(minusPlatformFee / (1 + this.employerFeePct)));
    }
    get totalRate() {
        return this.payRate + this.platformFeeRate + this.employerFeeRate;
    }
    get totalPay() {
        return Math.round(this.payRate * this.hoursWorked) + this.stipend;
    }
    get totalPlatformFee() {
        return Math.round(this.platformFeeRate * this.hoursWorked);
    }
    get totalEmployerFee() {
        return Math.round(this.employerFeeRate * this.hoursWorked);
    }
    get totalCost() {
        return this.totalPay + this.totalPlatformFee + this.totalEmployerFee;
    }
    get hoursWorked() {
        if (!this.minutesWorked) {
            throw new Error('Minutes worked must be greater than zero to calculate hours worked');
        }
        // ! round this only to two decimal places
        return (0, number_utils_1.roundToAtMostTwoDecimals)(this.minutesWorked / 60);
    }
    /**
     * Get all costs per hour.
     *
     * @param totalRate The $ rate, in pennies, as posted by the facility.
     * @param platformFeePct The percentage of base rate to charge for platform fee. Should be between 0.01 and 1.0.
     * @param employerFeePct The percentage of base rate to charge for employer fee. Should be between 0.01 and 1.0.
     * @returns {CalcualtedRates} Object of per hour costs.
     */
    static getCostsPerHour(totalRate, platformFeePct, employerFeePct) {
        const calc = new CostCalculator({
            inputRate: totalRate,
            platformFeePct,
            employerFeePct,
        });
        return {
            payRate: calc.payRate,
            platformFeeRate: calc.platformFeeRate,
            employerFeeRate: calc.employerFeeRate,
            totalRate: calc.totalRate,
        };
    }
    /**
     * Returns individual costs so they can be flexibly arranged on the invoice.
     *
     * @returns {CalculatedCosts} Object of total costs
     */
    static getTotalCosts(options) {
        const calc = new CostCalculator(options);
        return {
            pay: calc.totalPay,
            platformFee: calc.totalPlatformFee,
            employerFee: calc.totalEmployerFee,
            stipend: calc.stipend,
            total: calc.totalCost,
        };
    }
    static getTotalRateFromPayRate(payRate, platformFeePct, employerFeePct) {
        return payRate * (1 + platformFeePct) * (1 + employerFeePct);
    }
}
exports.default = CostCalculator;
//# sourceMappingURL=cost-calculator.js.map